/*
 * <collection-infinite-scroll>: loads the next page of a v3 collection grid as
 * the shopper nears the end of it. Built for an Intelligems test
 * (docs/intelligems-infinite-scroll/): it does nothing until the variant sets
 * data-infinite-scroll on <html>, so control keeps the server-rendered
 * pagination and so do crawlers and no-JS visitors.
 *
 * - The first `data-auto-loads` pages load on their own, about a screen and a
 *   half ahead of the end; after that a "Load more" button takes over, so the
 *   footer can be reached. A failed load stops auto-loading and shows the
 *   button as a retry.
 * - Rows for an incoming page are reserved with empty <li>s the height of a
 *   real card before the fetch starts, and filled in place, so nothing below
 *   moves when the cards land.
 * - Back from a product page puts the shopper where they were: pagehide saves
 *   the pages loaded and the first card on screen; on a back/forward or reload
 *   visit to the same URL the pages are reserved, the page scrolls to that
 *   card at once, and the cards fill in. Pages restored from the back/forward
 *   cache need none of this.
 * - It keeps iOS Safari's memory in check, because past ~100 cards a fast
 *   fling ran the tab out of memory and Safari reloaded the page (2026-10-23):
 *   during a fling only the rows are reserved, and the page is fetched once
 *   scrolling settles, with a pause between automatic loads; and cards more
 *   than three screens away give up their photos (see park()), which come
 *   back before the cards return to view. Cards keep their size throughout.
 * - It lives inside #ProductGridContainer, so every filter, pill or drawer
 *   swap replaces it with a fresh copy for the new results. Its state is kept
 *   in data attributes, because the quick filters can cache a snapshot of the
 *   grid (appended cards included) and put it back later.
 *
 * The section runs this file once per instance, so every declaration sits
 * inside the guard.
 */
if (!customElements.get('collection-infinite-scroll')) {
  const STORE_KEY = 'jnco:collection-infinite-scroll';
  const STORE_MAX_AGE = 30 * 60 * 1000;
  const LOOKAHEAD = '0px 0px 150% 0px';
  const END_EVENT = 'collection_end_reached';
  // How long scrolling must be quiet before an automatic load fetches, and the
  // least time between two automatic loads.
  const SETTLE_MS = 200;
  // Cards this far beyond the screen, either way, have their photos parked.
  const PARK_MARGIN = '300% 0px 300% 0px';
  const BLANK = 'data:image/gif;base64,R0lGODlhAQABAIAAAAAAAP///yH5BAEAAAAALAAAAAABAAEAAAIBRAA7';
  const PARKED = 'data-infinite-parked';

  // Swap a far-away card's photos for a blank image, so Safari can drop the
  // decoded bitmaps, and put them back as the card comes near. The image boxes
  // are sized by the card, so nothing moves.
  function park(card) {
    card.querySelectorAll(`.card-image-slider__img:not([${PARKED}])`).forEach((img) => {
      img.setAttribute(PARKED, img.getAttribute('src') || '');
      if (img.hasAttribute('srcset')) {
        img.setAttribute('data-infinite-srcset', img.getAttribute('srcset'));
        img.removeAttribute('srcset');
      }
      img.setAttribute('src', BLANK);
    });
  }

  function unpark(card) {
    card.querySelectorAll(`.card-image-slider__img[${PARKED}]`).forEach((img) => {
      const srcset = img.getAttribute('data-infinite-srcset');
      if (srcset !== null) {
        img.setAttribute('srcset', srcset);
        img.removeAttribute('data-infinite-srcset');
      }
      img.setAttribute('src', img.getAttribute(PARKED));
      img.removeAttribute(PARKED);
    });
  }

  // Intelligems analysis event, sent in BOTH arms: the shopper came within the
  // look-ahead of the end of the grid's first page, which is exactly where the
  // variant starts loading and control shows the page links. Results filtered
  // to visitors who sent it compare only people the variant could have reached.
  // Once per page view; it watches the server-rendered last card, so cards the
  // variant appends never move the point.
  function watchGridEnd(grid) {
    if (window.__collectionEndSent || !('IntersectionObserver' in window)) return null;
    const last = grid.querySelector(':scope > li:last-child');
    if (!last) return null;
    const observer = new IntersectionObserver(
      (entries) => {
        const reached = entries.some((entry) => entry.isIntersecting || entry.boundingClientRect.top < 0);
        if (!reached || window.__collectionEndSent) return;
        window.__collectionEndSent = true;
        observer.disconnect();
        window.igEvents = window.igEvents || [];
        window.igEvents.push({ event: END_EVENT });
      },
      { rootMargin: LOOKAHEAD }
    );
    observer.observe(last);
    return observer;
  }

  class CollectionInfiniteScroll extends HTMLElement {
    connectedCallback() {
      const container = this.closest('#ProductGridContainer');
      const grid = container && container.querySelector('#product-grid');
      if (grid) this.endObserver = watchGridEnd(grid);
      if (document.documentElement.hasAttribute('data-infinite-scroll')) {
        this.start();
        return;
      }
      this.onEnable = () => this.start();
      document.addEventListener('infinite-scroll:enable', this.onEnable);
    }

    disconnectedCallback() {
      if (this.onEnable) document.removeEventListener('infinite-scroll:enable', this.onEnable);
      if (this.observer) this.observer.disconnect();
      if (this.parkObserver) this.parkObserver.disconnect();
      if (this.onScroll) window.removeEventListener('scroll', this.onScroll);
      clearTimeout(this.settleTimer);
      if (this.endObserver) this.endObserver.disconnect();
      if (this.abortController) this.abortController.abort();
      if (this.onPageHide) window.removeEventListener('pagehide', this.onPageHide);
      if (this.onPageShow) window.removeEventListener('pageshow', this.onPageShow);
      if (this.onUserScroll) {
        ['wheel', 'touchstart', 'keydown'].forEach((type) => window.removeEventListener(type, this.onUserScroll));
      }
      this.started = false;
    }

    start() {
      if (this.started) return;
      const container = this.closest('#ProductGridContainer');
      this.grid = container && container.querySelector('#product-grid');
      if (!this.grid) return;
      this.started = true;

      this.pages = parseInt(this.dataset.pages, 10) || 1;
      this.total = parseInt(this.dataset.total, 10) || 0;
      this.startPage = parseInt(this.dataset.currentPage, 10) || 1;
      this.loadedThrough = parseInt(this.dataset.loadedThrough, 10) || this.startPage;
      this.autoLeft =
        this.dataset.autoLeft !== undefined
          ? parseInt(this.dataset.autoLeft, 10) || 0
          : parseInt(this.dataset.autoLoads, 10) || 0;
      this.loading = false;
      this.failed = false;
      this.lastScroll = 0;
      this.quietUntil = 0;
      this.pendingSlots = null;
      this.onScroll = () => {
        this.lastScroll = Date.now();
      };
      window.addEventListener('scroll', this.onScroll, { passive: true });

      this.button = this.querySelector('[data-infinite-button]');
      this.label = this.querySelector('[data-infinite-label]');
      this.status = this.querySelector('[data-infinite-status]');

      // A snapshot taken mid-load can carry reserved rows; they would never fill.
      this.grid.querySelectorAll('[data-infinite-placeholder]').forEach((li) => li.remove());
      this.watchCards(this.grid.querySelectorAll(':scope > li'));

      this.setAttribute('data-active', '');
      try {
        history.scrollRestoration = 'manual';
      } catch (e) {}

      this.onPageHide = () => this.saveState();
      window.addEventListener('pagehide', this.onPageHide);
      // A page restored from the back/forward cache keeps its cards, but with
      // scrollRestoration 'manual' iOS Safari 26.5 doesn't keep its position
      // (it landed near the last focused card). Put the saved card back.
      this.onPageShow = (event) => {
        if (!event.persisted) return;
        const state = this.readState(true);
        if (state && state.loadedThrough === this.loadedThrough) this.scrollToCard(state.index, state.offset);
      };
      window.addEventListener('pageshow', this.onPageShow);

      if (this.button) this.button.addEventListener('click', () => this.onButtonClick());

      this.render();
      if (!this.restore()) this.observe();
    }

    get hasMore() {
      return this.loadedThrough < this.pages;
    }

    cardCount() {
      return this.grid.querySelectorAll(':scope > li:not([data-infinite-placeholder])').length;
    }

    render() {
      this.dataset.loadedThrough = String(this.loadedThrough);
      this.dataset.autoLeft = String(this.autoLeft);
      if (!this.button) return;
      const showButton = this.hasMore && (this.failed || this.autoLeft <= 0 || this.loading);
      this.button.hidden = !showButton;
      if (this.loading) {
        this.button.setAttribute('aria-disabled', 'true');
        this.label.textContent = this.dataset.loadingText;
      } else {
        this.button.removeAttribute('aria-disabled');
        this.label.textContent = this.failed ? this.dataset.errorText : this.dataset.buttonText;
      }
    }

    announce() {
      if (!this.status) return;
      this.status.textContent = this.dataset.statusText
        .replace('[shown]', String(this.cardCount()))
        .replace('[total]', String(this.total));
    }

    observe() {
      if (this.observer || !('IntersectionObserver' in window)) return;
      this.observer = new IntersectionObserver(
        (entries) => {
          if (entries.some((entry) => entry.isIntersecting)) this.maybeAutoLoad();
        },
        { rootMargin: LOOKAHEAD }
      );
      this.observer.observe(this);
    }

    // The observer only fires on a change, so after a load that leaves this
    // element inside the look-ahead (a tall screen, a short page) check again.
    // Rows reserved ahead of a fetch push this element down, so while there
    // are any, the end of the grid is where they start.
    loadPoint() {
      return (this.pendingSlots && this.pendingSlots[0]) || this;
    }

    nearEnd() {
      const rect = this.loadPoint().getBoundingClientRect();
      return rect.top < window.innerHeight * 2.5;
    }

    // One observer for every card: park the photos of cards far off screen.
    watchCards(cards) {
      if (!('IntersectionObserver' in window)) return;
      if (!this.parkObserver) {
        this.parkObserver = new IntersectionObserver(
          (entries) => {
            entries.forEach((entry) => {
              if (entry.isIntersecting) unpark(entry.target);
              else park(entry.target);
            });
          },
          { rootMargin: PARK_MARGIN }
        );
      }
      cards.forEach((card) => this.parkObserver.observe(card));
    }

    maybeAutoLoad() {
      if (this.loading || this.failed || !this.hasMore || this.autoLeft <= 0) return;
      // Mid-fling, reserve the rows now (the footer stays out of reach) but
      // fetch and parse the page only once scrolling has settled.
      const wait = Math.max(this.lastScroll + SETTLE_MS, this.quietUntil) - Date.now();
      if (wait > 0) {
        if (!this.pendingSlots) {
          this.pendingSlots = this.reserve(this.slotsForPage());
          if (this.observer && this.pendingSlots[0]) this.observer.observe(this.pendingSlots[0]);
        }
        clearTimeout(this.settleTimer);
        this.settleTimer = setTimeout(() => {
          if (this.isConnected && this.nearEnd()) this.maybeAutoLoad();
        }, wait);
        return;
      }
      const slots = this.takePendingSlots();
      this.autoLeft -= 1;
      this.load(slots).then((ok) => {
        this.quietUntil = Date.now() + SETTLE_MS;
        if (ok && this.isConnected && this.nearEnd()) this.maybeAutoLoad();
      });
    }

    takePendingSlots() {
      const slots = this.pendingSlots;
      this.pendingSlots = null;
      if (slots && slots[0] && this.observer) this.observer.unobserve(slots[0]);
      return slots;
    }

    onButtonClick() {
      if (this.loading || !this.hasMore) return;
      this.failed = false;
      clearTimeout(this.settleTimer);
      this.load(this.takePendingSlots()).then((ok) => {
        if (ok && this.firstNewCard) {
          // Keyboard and screen-reader users continue from the first new
          // product rather than from a button that may now be gone. The
          // global focus wrapper keeps the ring off after a tap or click.
          // The card renders two title links, one hidden by layout; take the
          // one on screen, else the first link in the card.
          const titles = Array.from(this.firstNewCard.querySelectorAll('.card__heading a[href]'));
          const link =
            titles.find((a) => a.getClientRects().length > 0) || this.firstNewCard.querySelector('a[href]');
          if (link) link.focus({ preventScroll: true });
        }
      });
    }

    pageUrl(page) {
      const params = new URLSearchParams(window.location.search);
      params.set('page', String(page));
      params.set('section_id', this.grid.dataset.id);
      return `${window.location.pathname}?${params.toString()}`;
    }

    rowHeight() {
      const card = this.grid.querySelector(':scope > li:not([data-infinite-placeholder])');
      return card ? card.offsetHeight : 0;
    }

    // Empty <li>s keep their box (base.css hides empty divs, not list items),
    // and sitting in the grid they take its columns and gaps.
    reserve(count) {
      const height = this.rowHeight();
      const slots = [];
      const fragment = document.createDocumentFragment();
      for (let i = 0; i < count; i += 1) {
        const li = document.createElement('li');
        li.className = 'grid__item collection-infinite-scroll__slot';
        li.setAttribute('data-infinite-placeholder', '');
        li.setAttribute('aria-hidden', 'true');
        if (height) li.style.height = `${height}px`;
        fragment.appendChild(li);
        slots.push(li);
      }
      this.grid.appendChild(fragment);
      return slots;
    }

    remainingCount() {
      const reserved = this.grid.querySelectorAll(':scope > [data-infinite-placeholder]').length;
      return Math.max(0, this.total - this.cardCount() - reserved);
    }

    slotsForPage() {
      const perPage = parseInt(this.dataset.perPage, 10) || 36;
      return Math.min(perPage, this.remainingCount());
    }

    fetchPage(page) {
      return fetch(this.pageUrl(page), { signal: this.abortController.signal })
        .then((response) => {
          if (!response.ok) throw new Error(`HTTP ${response.status}`);
          return response.text();
        })
        .then((html) => new DOMParser().parseFromString(html, 'text/html'));
    }

    // Prepare a fetched page's cards for a grid that is already on screen.
    cardsFrom(doc) {
      const known = new Set(
        Array.from(this.grid.querySelectorAll('[data-product-id]')).map((el) => el.getAttribute('data-product-id'))
      );
      const cards = [];
      doc.querySelectorAll('#product-grid > li').forEach((li) => {
        const idEl = li.querySelector('[data-product-id]');
        const id = idEl && idEl.getAttribute('data-product-id');
        if (id && known.has(id)) return;
        if (id) known.add(id);
        // The page's first card carries the card stylesheets and scripts; the
        // page already has them, and inserted scripts never run. Keep JSON data
        // blocks: the Quick Add overlay reads its sizes from one in every card.
        li.querySelectorAll('script:not([type="application/json"]), link[rel="stylesheet"]').forEach((el) =>
          el.remove()
        );
        // Each page marks its first two images as the LCP candidates.
        li.querySelectorAll('img').forEach((img) => {
          img.setAttribute('loading', 'lazy');
          img.removeAttribute('fetchpriority');
        });
        // Show at once instead of waiting on the reveal animation (and no
        // Safari flash of the animation's start state).
        if (li.classList.contains('scroll-trigger')) li.classList.add('scroll-trigger--cancel');
        li.querySelectorAll('.scroll-trigger').forEach((el) => el.classList.add('scroll-trigger--cancel'));
        cards.push(document.importNode(li, true));
      });
      const next = doc.querySelector('collection-infinite-scroll');
      const pages = next && parseInt(next.dataset.pages, 10);
      return { cards, pages };
    }

    fill(slots, cards) {
      const fragment = document.createDocumentFragment();
      cards.forEach((card) => fragment.appendChild(card));
      if (slots.length && slots[0].parentNode) {
        slots[0].parentNode.insertBefore(fragment, slots[0]);
      } else {
        this.grid.appendChild(fragment);
      }
      slots.forEach((slot) => slot.remove());
      this.watchCards(cards);
      if (typeof window.initializeQuickAdd === 'function') window.initializeQuickAdd();
      document.dispatchEvent(new CustomEvent('collection:products-appended', { detail: { cards } }));
    }

    load(reserved) {
      const page = this.loadedThrough + 1;
      this.loading = true;
      this.render();
      this.grid.setAttribute('aria-busy', 'true');
      if (!this.abortController) this.abortController = new AbortController();
      const slots = reserved || this.reserve(this.slotsForPage());

      return this.fetchPage(page)
        .then((doc) => {
          const { cards, pages } = this.cardsFrom(doc);
          this.fill(slots, cards);
          this.firstNewCard = cards[0] || null;
          this.loadedThrough = page;
          if (pages) this.pages = pages;
          this.loading = false;
          this.render();
          this.announce();
          return true;
        })
        .catch((error) => {
          slots.forEach((slot) => slot.remove());
          this.loading = false;
          if (error && error.name === 'AbortError') return false;
          this.failed = true;
          this.autoLeft = 0;
          this.render();
          return false;
        })
        .finally(() => {
          if (this.grid) this.grid.removeAttribute('aria-busy');
        });
    }

    stateKey() {
      const params = new URLSearchParams(window.location.search);
      params.delete('page');
      return `${window.location.pathname}?${params.toString()}`;
    }

    saveState() {
      if (!this.grid || !this.isConnected) return;
      const cards = Array.from(this.grid.children);
      let index = 0;
      let offset = 0;
      for (let i = 0; i < cards.length; i += 1) {
        const rect = cards[i].getBoundingClientRect();
        if (rect.bottom > 0) {
          index = i;
          offset = rect.top;
          break;
        }
      }
      try {
        sessionStorage.setItem(
          STORE_KEY,
          JSON.stringify({
            key: this.stateKey(),
            startPage: this.startPage,
            loadedThrough: this.loadedThrough,
            autoLeft: this.autoLeft,
            index,
            offset,
            time: Date.now(),
          })
        );
      } catch (e) {}
    }

    readState(fromCache = false) {
      let nav = '';
      try {
        const entry = performance.getEntriesByType('navigation')[0];
        nav = entry ? entry.type : '';
      } catch (e) {}
      if (!fromCache && nav !== 'back_forward' && nav !== 'reload') return null;
      let state = null;
      try {
        state = JSON.parse(sessionStorage.getItem(STORE_KEY) || 'null');
      } catch (e) {}
      if (!state || state.key !== this.stateKey() || state.startPage !== this.startPage) return null;
      if (Date.now() - state.time > STORE_MAX_AGE) return null;
      return state;
    }

    scrollToCard(index, offset) {
      const card = this.grid.children[Math.min(index, this.grid.children.length - 1)];
      if (!card) return;
      const top = card.getBoundingClientRect().top + window.scrollY - offset;
      window.scrollTo({ top: Math.max(0, top), behavior: 'instant' });
    }

    // Returns true when it took over (it starts observing once done).
    restore() {
      const state = this.readState();
      if (!state) return false;
      // Restore only once per visit: a pill click rebuilds this element for
      // other results, and that copy must start fresh.
      if (window.__collectionInfiniteRestored) return false;
      window.__collectionInfiniteRestored = true;

      const target = Math.min(state.loadedThrough, this.pages);
      const pages = [];
      for (let page = this.loadedThrough + 1; page <= target; page += 1) pages.push(page);

      let userScrolled = false;
      this.onUserScroll = () => {
        userScrolled = true;
      };
      ['wheel', 'touchstart', 'keydown'].forEach((type) =>
        window.addEventListener(type, this.onUserScroll, { passive: true, once: true })
      );

      if (!pages.length) {
        this.scrollToCard(state.index, state.offset);
        this.observe();
        return true;
      }

      this.autoLeft = Math.max(0, parseInt(state.autoLeft, 10) || 0);
      this.loading = true;
      this.render();
      this.grid.setAttribute('aria-busy', 'true');
      this.abortController = new AbortController();

      // Reserve every page first so the scroll lands now, then fill in order.
      const groups = pages.map(() => this.reserve(this.slotsForPage()));
      this.scrollToCard(state.index, state.offset);

      Promise.all(pages.map((page) => this.fetchPage(page).catch((error) => error)))
        .then((docs) => {
          if (!this.isConnected) return;
          let reached = this.loadedThrough;
          docs.forEach((doc, i) => {
            const failed = !(doc instanceof Document) || reached !== pages[i] - 1;
            if (failed) {
              groups[i].forEach((slot) => slot.remove());
              if (!(doc && doc.name === 'AbortError')) this.failed = true;
              return;
            }
            const { cards, pages: total } = this.cardsFrom(doc);
            this.fill(groups[i], cards);
            if (total) this.pages = total;
            reached = pages[i];
          });
          this.loadedThrough = reached;
          if (this.failed) this.autoLeft = 0;
          this.loading = false;
          this.grid.removeAttribute('aria-busy');
          this.render();
          if (!userScrolled) this.scrollToCard(state.index, state.offset);
          this.observe();
        });
      return true;
    }
  }

  customElements.define('collection-infinite-scroll', CollectionInfiniteScroll);
}
